import type { ActionProposalArtifact, ActionProposalItem } from "~/shared/ai/assistant-action-artifact";
import { Button, Text } from "~/ui";
import { convertDateToString } from "~/utils/datetime";

import { BLOCK } from "./action-block/action-block-tokens";
import { ASSISTANT_ACTION_COPY } from "./assistant-action-copy";

/** "Saved by Jane, Oct 27": the receipt's one metadata line, its day in the org's calendar; null with no recorded approver. */
export function receiptAttribution(artifact: ActionProposalArtifact, orgTimezone: string): string | null {
  const approval = artifact.approval;
  if (!approval?.actorDisplayName) {
    return null;
  }
  const day = convertDateToString(new Date(approval.approvedAt), orgTimezone, "LLL d");
  return ASSISTANT_ACTION_COPY.savedBy(approval.actorDisplayName, day);
}

/** An org-relative link from the server, under this org's slug. */
export const orgHref = (orgSlug: string, href: string): string => `/${encodeURIComponent(orgSlug)}${href}`;

/** A saved row's route to its record, when the receipt's header destination cannot land on several (Cost, until it can). */
export function receiptRowLink(item: ActionProposalItem | undefined, orgSlug: string): { href: string; title?: string } | null {
  const link = item?.status === "APPLIED" ? item.links?.[0] : undefined;
  return link ? { href: orgHref(orgSlug, link.href), ...(link.title ? { title: link.title } : {}) } : null;
}

/** One quiet "Correct" on a saved row that can be corrected, or the row's note when its record cannot be linked. */
export const ReceiptRowCorrect = ({
  item,
  correctLabel,
  onCorrect,
}: {
  item: ActionProposalItem | undefined;
  correctLabel: string;
  onCorrect?: (item: ActionProposalItem) => void;
}) => {
  if (!item || item.status !== "APPLIED") {
    return null;
  }
  return (
    <>
      {item.correctable && onCorrect && (
        <Button
          size="compact-sm"
          variant="subtle"
          color="gray"
          px={0}
          style={{ fontSize: BLOCK.meta, fontWeight: 500, color: BLOCK.secondary, whiteSpace: "nowrap" }}
          aria-label={correctLabel}
          title={correctLabel}
          onClick={() => onCorrect(item)}
        >
          Correct
        </Button>
      )}
      {!item.links?.length && item.linkNote && <Text style={{ fontSize: BLOCK.meta, color: BLOCK.secondary }}>{item.linkNote}</Text>}
    </>
  );
};
